import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useCurrentUser } from '../../hooks/useApi';
import { Box, CircularProgress, Typography } from '@mui/material';

interface PrivilegedDocsGuardProps {
  children?: React.ReactNode;
}

interface Category {
  id: string;
  title: string;
  requiresPermission: string | null;
}

interface Metadata {
  categories: Category[];
}

/**
 * Route guard for privileged documentation
 * Dynamically checks the required permission based on the category
 */
export const PrivilegedDocsGuard: React.FC<PrivilegedDocsGuardProps> = ({ children }) => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const [metadata, setMetadata] = useState<Metadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);

  // Load metadata to determine required permission
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const response = await fetch('/docs/metadata.json');
        const data = await response.json();
        setMetadata(data);
      } catch (error) {
        console.error('Error loading metadata:', error);
      } finally {
        setMetadataLoading(false);
      }
    };

    loadMetadata();
  }, []);

  // Show loading state while checking permissions
  if (userLoading || metadataLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Checking permissions...
        </Typography>
      </Box>
    );
  }

  // Find the category to determine required permission
  const category = metadata?.categories.find(cat => cat.id === categoryId);

  // If category not found or no permission specified, allow access
  // (This shouldn't happen in normal flow, but provides a fallback)
  if (!category || !category.requiresPermission) {
    return <>{children || <Outlet />}</>;
  }

  // Check if user has the required permission for this category
  if (!user?.permissions?.includes(category.requiresPermission)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children || <Outlet />}</>;
};
